import React, { useCallback, useEffect, useState } from "react";
import { fetchProjectStructure } from "../../api/projectStructureApi";
import { WorkbookSyncError, buildWorkbook, saveWorkbook } from "../../excel/v2/syncProject";
import { workbookSheets } from "../../structure/structureRules";
import { confirmDialog } from "../../utils/confirmDialog";

/** Project page tab: the plot/structure summary and the Excel workbook of a v2 project. */
export default function StructureTab({ token, project, canEdit, onOpenSetup, refreshKey }) {
  const [structure, setStructure] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      setStructure(await fetchProjectStructure(token, project.id));
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    }
  }, [token, project.id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const run = async (label, fn) => {
    setBusy(label);
    setMessage(null);
    try {
      const text = await fn();
      if (text) setMessage({ kind: "ok", text });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof WorkbookSyncError ? err.message : `შეცდომა: ${err.message}` });
    } finally {
      setBusy("");
    }
  };

  const build = () =>
    run("build", async () => {
      const r = await buildWorkbook({
        token,
        project,
        confirmRemove: (list) =>
          confirmDialog(
            `ეს ფურცლები სტრუქტურაში აღარ არის და წაიშლება სამუშაოებთან ერთად:\n${list
              .filter((s) => s.rows)
              .map((s) => `${s.name} — ${s.rows} ხაზი`)
              .join("\n")}`,
            { confirmLabel: "წაშლა", cancelLabel: "გაუქმება" }
          ),
        onProgress: (done, total) => setBusy(`build ${done}/${total}`),
      });
      return r.cancelled ? "გაუქმდა — Excel არ შეცვლილა." : `Excel განახლდა: ${r.written} ფურცელი${r.removed ? `, წაიშალა ${r.removed}` : ""}; შენახულია ${r.savedRows} ხაზი.`;
    });

  const save = () =>
    run("save", async () => {
      const r = await saveWorkbook({ token, project });
      return `შენახულია: ${r.sheets} ფურცელი, ${r.rows} ხაზი.`;
    });

  const objects = structure?.exists ? workbookSheets(structure.site, structure.trees, { frozen: !!structure.settings?.frozen }) : [];
  const sheetCount = objects.reduce((s, o) => s + o.sheets.length + 1, 0);

  return (
    <div className="structure-tab" data-no-translate="true" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {!structure && <div>იტვირთება…</div>}
      {structure && !structure.exists && (
        <div className="msg-info">ამ პროექტს ნაკვეთი და სტრუქტურა ჯერ არ აქვს. გახსენით ფანჯარა და შეინახეთ — შემდეგ Excel აეწყობა მის მიხედვით.</div>
      )}
      {structure?.exists && (
        <div>
          <b>{objects.length} ობიექტი · {sheetCount} ფურცელი</b>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {objects.map((o) => (
              <li key={o.objectId}>
                {o.total} — {o.name}: {o.sheets.length} ფურცელი
              </li>
            ))}
          </ul>
          {structure.updated_by && (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              ბოლოს შეცვალა {structure.updated_by} (ვერსია {structure.revision})
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="boq-action-btn" onClick={onOpenSetup} disabled={!!busy}>
          ⚙ ნაკვეთი და სტრუქტურა
        </button>
        {structure?.exists && (
          <>
            <button type="button" className="primary" onClick={build} disabled={!!busy || !canEdit}>
              {busy.startsWith("build") ? `იწერება… ${busy.slice(6)}` : "↧ Excel-ის აწყობა / განახლება"}
            </button>
            <button type="button" className="boq-action-btn" onClick={save} disabled={!!busy || !canEdit}>
              {busy === "save" ? "ინახება…" : "✓ შენახვა Excel-იდან"}
            </button>
          </>
        )}
      </div>
      <div style={{ fontSize: 12, opacity: 0.75 }}>
        „აწყობა / განახლება“ ქმნის და ანახლებს TOTAL-ს და დისციპლინების ფურცლებს. უკვე არსებული ფურცლების სამუშაოები ინარჩუნდება — მათი
        შიგთავსი ჯერ წაიკითხება Excel-იდან.
      </div>
      {message && <div className={message.kind === "ok" ? "msg-success" : "msg-error"}>{message.text}</div>}
    </div>
  );
}
